import { cloneDeep, flatMap, lowerFirst } from "lodash";
import { createEmptyEventInstance, getEventModelName, isEventInstanceEmpty } from "@/entities/data-model";
import type { EventInstanceContent, Relations } from "@/entities/data-model";
import type {
    EventFieldsGroup,
    FamilyRelation,
    FormMetadataResponse,
    NameFieldGroup,
    OtherFieldGroup,
    RelationshipFieldGroup,
} from "@/entities/form-metadata";
import {
    EventAssertion,
    FormLevelFeatureType,
    GroupType,
    NameAssertion,
    SpecialAssertion,
    getRowEventIdentities,
} from "@/entities/form-metadata";
import type { Person } from "@/entities/tree-person";
import { LifeEventType, Relationship } from "@/entities/tree-person";

const FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING = {
    [Relationship.Father]: "father",
    [Relationship.Mother]: "mother",
    [Relationship.Spouse]: "spouse",
    [Relationship.Sibling]: "sibling",
    [Relationship.Child]: "child",
} as const;

const cleanFamilyMemberInstances = (currentFamilyMembers: Relations, familyMembers: Person["FamilyMembers"]) => {
    familyMembers.forEach((familyMember) => {
        if (FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]) {
            currentFamilyMembers[FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]].instances = [];
        }
    });
};

const isEmptyFamilyMember = (familyMember: Person["FamilyMembers"][number]) =>
    !familyMember.GivenName && !familyMember.Surname;

const populateFamilyMemberInstances = (
    currentFamilyMembers: Relations,
    familyMembers: Person["FamilyMembers"],
    allowedFamilyMembersObjects: Map<ReturnType<typeof lowerFirst<FamilyRelation>>, Set<NameAssertion>>,
    maxInstancesMap: Map<ReturnType<typeof lowerFirst<FamilyRelation>>, number>,
) => {
    familyMembers.forEach((familyMember) => {
        if (
            FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship] &&
            allowedFamilyMembersObjects.has(FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship]) &&
            !isEmptyFamilyMember(familyMember)
        ) {
            const key = FAMILY_RELATIONSHIP_TO_DETAILS_MAPPING[familyMember.Relationship];
            const max = maxInstancesMap.get(key) ?? Infinity;
            if (currentFamilyMembers[key].instances.length >= max) return;

            const allowedFamilyMemberFieldsObject = allowedFamilyMembersObjects.get(key);
            const isGivenNameAllowed = allowedFamilyMemberFieldsObject?.has(NameAssertion.GivenName);
            const isSurnameAllowed = allowedFamilyMemberFieldsObject?.has(NameAssertion.Surname);
            // @ts-ignore FIXME
            currentFamilyMembers[key].instances.push({
                givenName: isGivenNameAllowed ? familyMember.GivenName : "",
                surname: isSurnameAllowed ? familyMember.Surname : "",
                secondSurname: "",
                surnameExactness: {
                    flags: {
                        phonetic: false,
                        similar: false,
                        soundex: false,
                    },
                    isExact: false,
                },
                givenNameExactness: {
                    flags: {
                        phonetic: false,
                        similar: false,
                        initials: false,
                    },
                    isExact: false,
                },
            });
        }
    });
};

const obtainFamilyMembersGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) =>
            "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.FamilyMember,
    ) as RelationshipFieldGroup[];
const obtainAllowedFamilyMemberFields = (familyMembersGroups: RelationshipFieldGroup[]) => {
    const allowedFamilyMembersObjects = new Map<ReturnType<typeof lowerFirst<FamilyRelation>>, Set<NameAssertion>>();
    const familyMember = flatMap(familyMembersGroups, (familyMembersGroup) => familyMembersGroup.fieldsets);
    familyMember.forEach((relationship) => {
        const currentStateOfSet = allowedFamilyMembersObjects.get(lowerFirst(relationship.relation)) ?? new Set();
        const unwrappedFamilyMemberFields = new Set(flatMap(relationship.fields, (field) => field.assertion));
        allowedFamilyMembersObjects.set(
            lowerFirst(relationship.relation),
            new Set([...Array.from(currentStateOfSet), ...Array.from(unwrappedFamilyMemberFields)]),
        );
    });

    return allowedFamilyMembersObjects;
};

const obtainMaxFamilyMemberInstances = (familyMembersGroups: RelationshipFieldGroup[], currentFamily: Relations) => {
    const maxInstancesMap = new Map<ReturnType<typeof lowerFirst<FamilyRelation>>, number>();
    familyMembersGroups.forEach((group) => {
        if (group.fieldsetDisplayStrategy !== "prefilled-static") return;
        group.fieldsets.forEach((fieldset) => {
            const key = lowerFirst(fieldset.relation) as keyof Relations;
            if (currentFamily[key]) {
                maxInstancesMap.set(key, currentFamily[key].instances.length);
            }
        });
    });
    return maxInstancesMap;
};

export const getFamilyMemberDetails = <T extends { family: Relations }>(
    familyMembers: Person["FamilyMembers"],
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["family"] => {
    const familyMembersGroups = obtainFamilyMembersGroups(formMetadata);
    const allowedFamilyMembersObjects = obtainAllowedFamilyMemberFields(familyMembersGroups);
    const maxInstancesMap = obtainMaxFamilyMemberInstances(familyMembersGroups, currentFormValue.family);

    const currentFamilyMembers = cloneDeep(currentFormValue.family);
    cleanFamilyMemberInstances(currentFamilyMembers, familyMembers);
    populateFamilyMemberInstances(currentFamilyMembers, familyMembers, allowedFamilyMembersObjects, maxInstancesMap);

    const dynamicRelations = new Set<keyof Relations>();
    familyMembersGroups.forEach((group) => {
        if (group.fieldsetDisplayStrategy === "prefilled-dynamic") {
            group.fieldsets.forEach((fieldset) => {
                dynamicRelations.add(lowerFirst(fieldset.relation) as keyof Relations);
            });
        }
    });

    for (const key of dynamicRelations) {
        currentFamilyMembers[key].instances = currentFamilyMembers[key].instances.filter(
            (instance) => instance.givenName || instance.surname,
        );
    }

    return currentFamilyMembers;
};

const EVENT_TO_DETAILS_MAPPING = {
    [LifeEventType.Birth]: "SelfBirth",
    [LifeEventType.Death]: "SelfDeath",
    [LifeEventType.Marriage]: "SelfMarriage",
    [LifeEventType.Residence]: "SelfResidence",
    [LifeEventType.Any]: "Self",
} as const;

/** The entries quick-fill reads and writes, keyed by model name (`SelfDeath`, ...). */
type EventEntries = Record<string, { instances: EventInstanceContent[] }>;

/**
 * How a life event's fields bind to entries, keyed by the entry the event maps to. A row's
 * fields may each name their own event (a Death row whose Place is a Burial field), so
 * coupled entries move in lockstep: instance N of `SelfDeath` and `SelfBurial` are one row.
 */
interface EventRowBinding {
    /** The entry the field carrying each assertion writes to. */
    entryByAssertion: Map<EventAssertion, string>;
    /** Every entry the rows binding this event touch, in field order. */
    coupledEntries: string[];
}

const obtainEventRowBindings = (eventGroups: EventFieldsGroup[]) => {
    const bindings = new Map<string, EventRowBinding>();
    const fieldsets = flatMap(eventGroups, (eventGroup) => eventGroup.fieldsets);

    fieldsets.forEach((fieldset) => {
        const rowEntries = getRowEventIdentities(fieldset)
            .map(getEventModelName)
            .filter((entry): entry is string => Boolean(entry));

        rowEntries.forEach((entry) => {
            const binding: EventRowBinding = bindings.get(entry) ?? { entryByAssertion: new Map(), coupledEntries: [] };
            fieldset.fields.forEach((field) => {
                const fieldEntry = getEventModelName(field);
                if (!fieldEntry) return;
                // An entry's own field wins over a substitute from another row that also binds it.
                if (fieldEntry === entry || !binding.entryByAssertion.has(field.assertion)) {
                    binding.entryByAssertion.set(field.assertion, fieldEntry);
                }
            });
            rowEntries.forEach((coupled) => {
                if (!binding.coupledEntries.includes(coupled)) binding.coupledEntries.push(coupled);
            });
            bindings.set(entry, binding);
        });
    });

    return bindings;
};

const cleanEventInstances = (
    currentEvents: EventEntries,
    lifeEvents: Person["LifeEvents"],
    bindings: Map<string, EventRowBinding>,
) => {
    lifeEvents.forEach((lifeEvent) => {
        const eventKey = EVENT_TO_DETAILS_MAPPING[lifeEvent.Type];
        if (!eventKey) return;
        // Coupled entries are cleared together so positions stay aligned across the row.
        const entries = new Set([eventKey, ...(bindings.get(eventKey)?.coupledEntries ?? [])]);
        entries.forEach((entry) => {
            const target = currentEvents[entry];
            if (target) target.instances = [];
        });
    });
};

const isEmptyEvent = (lifeEvent: Person["LifeEvents"][number]) =>
    lifeEvent.Date.Year === null &&
    lifeEvent.Date.Month === null &&
    lifeEvent.Date.Day === null &&
    !lifeEvent.LocationText;

const SINGLETON_EVENT_TYPES: ReadonlySet<string> = new Set([
    LifeEventType.Birth,
    LifeEventType.Death,
    LifeEventType.Marriage,
]);

const shouldSkipEvent = (lifeEvent: Person["LifeEvents"][number]) =>
    isEmptyEvent(lifeEvent) || (SINGLETON_EVENT_TYPES.has(lifeEvent.Type) && lifeEvent.Date.Year === null);

const populateEventInstances = (
    currentEvents: EventEntries,
    lifeEvents: Person["LifeEvents"],
    bindings: Map<string, EventRowBinding>,
    maxInstancesMap: Map<string, number>,
    defaultYearProximity: number | null = null,
) => {
    lifeEvents.forEach((lifeEvent) => {
        const eventKey = EVENT_TO_DETAILS_MAPPING[lifeEvent.Type];
        const binding = eventKey ? bindings.get(eventKey) : undefined;
        if (!eventKey || !binding || !currentEvents[eventKey] || shouldSkipEvent(lifeEvent)) return;

        const max = maxInstancesMap.get(eventKey) ?? Infinity;
        if (currentEvents[eventKey].instances.length >= max) return;

        const gpid = lifeEvent.LocationGpids?.Gpid != null ? String(lifeEvent.LocationGpids.Gpid) : "";

        // Each part of the event goes to the entry whose field carries it; other coupled
        // entries gain an empty instance at the same position so the row stays whole.
        binding.coupledEntries.forEach((entry) => {
            const target = currentEvents[entry];
            if (!target) return;
            const owns = (assertion: EventAssertion) => binding.entryByAssertion.get(assertion) === entry;
            const empty = createEmptyEventInstance(defaultYearProximity);
            target.instances.push({
                ...empty,
                date: {
                    ...empty.date,
                    year: owns(EventAssertion.Year) ? lifeEvent.Date.Year : null,
                    month: owns(EventAssertion.Month) ? lifeEvent.Date.Month : null,
                    day: owns(EventAssertion.Day) ? lifeEvent.Date.Day : null,
                },
                gpid: owns(EventAssertion.Place) ? gpid : "",
                location: owns(EventAssertion.Place) ? lifeEvent.LocationText : "",
            });
        });
    });
};

const obtainEventGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) =>
            "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.Event,
    ) as EventFieldsGroup[];

/** The entries a row binds that the form actually holds, in field order. */
const obtainRowEntries = (fieldset: EventFieldsGroup["fieldsets"][number], currentEvents: EventEntries) =>
    getRowEventIdentities(fieldset)
        .map(getEventModelName)
        .filter((entry): entry is string => entry !== null && entry !== "" && currentEvents[entry] !== undefined);

const obtainMaxEventInstances = (eventGroups: EventFieldsGroup[], currentEvents: EventEntries) => {
    const maxInstancesMap = new Map<string, number>();
    eventGroups.forEach((eventGroup) => {
        if (eventGroup.fieldsetDisplayStrategy !== "prefilled-static") return;
        eventGroup.fieldsets.forEach((fieldset) => {
            obtainRowEntries(fieldset, currentEvents).forEach((entry) => {
                maxInstancesMap.set(entry, currentEvents[entry]!.instances.length);
            });
        });
    });
    return maxInstancesMap;
};

interface EventRow {
    entries: string[];
    fieldsetDisplayStrategy: EventFieldsGroup["fieldsetDisplayStrategy"];
}

/** Groups rows into sets that must move together: any two rows sharing an entry, transitively, are one set. */
const groupCoupledRows = (rows: EventRow[]): EventRow[][] => {
    const groups: EventRow[][] = [];
    rows.forEach((row) => {
        const touching = groups.filter((group) =>
            group.some((other) => other.entries.some((entry) => row.entries.includes(entry))),
        );
        const merged = [...touching.flat(), row];
        touching.forEach((group) => groups.splice(groups.indexOf(group), 1));
        groups.push(merged);
    });
    return groups;
};

/**
 * Keeps every row's entries in lockstep: instance N exists in each entry a row binds, padded
 * where one entry is shorter. A prefilled-static row keeps a floor of one position even when
 * empty (it's always rendered); a prefilled-dynamic row drops positions empty in every entry.
 * Positions are decided per coupled set of rows so stripping one row can't affect another
 * that still renders the same entry.
 */
const alignEventRows = (
    currentEvents: EventEntries,
    eventGroups: EventFieldsGroup[],
    defaultYearProximity: number | null,
) => {
    const rows = eventGroups.flatMap((eventGroup) =>
        eventGroup.fieldsets.map(
            (fieldset): EventRow => ({
                entries: obtainRowEntries(fieldset, currentEvents),
                fieldsetDisplayStrategy: eventGroup.fieldsetDisplayStrategy,
            }),
        ),
    );

    groupCoupledRows(rows.filter((row) => row.entries.length > 0)).forEach((coupledRows) => {
        const positions = new Set<number>();
        coupledRows.forEach(({ entries, fieldsetDisplayStrategy }) => {
            const minRowLength = fieldsetDisplayStrategy === "prefilled-static" ? 1 : 0;
            const stripEmpty = fieldsetDisplayStrategy === "prefilled-dynamic";
            const rowLength = Math.max(minRowLength, ...entries.map((entry) => currentEvents[entry]!.instances.length));
            for (let position = 0; position < rowLength; position++) {
                const filled = entries.some((entry) => {
                    const instance = currentEvents[entry]!.instances[position];
                    return instance !== undefined && !isEventInstanceEmpty(instance);
                });
                if (!stripEmpty || filled) positions.add(position);
            }
        });

        const keptPositions = Array.from(positions).sort((a, b) => a - b);
        const entries = new Set(coupledRows.flatMap((row) => row.entries));
        entries.forEach((entry) => {
            const target = currentEvents[entry]!;
            target.instances = keptPositions.map(
                (position) => target.instances[position] ?? createEmptyEventInstance(defaultYearProximity),
            );
        });
    });
};

const prepareLifeEvents = (lifeEvents: Person["LifeEvents"], formMetadata: FormMetadataResponse) => {
    if (formMetadata.contentType !== "collection") return lifeEvents;

    const nonResidence: Person["LifeEvents"] = [];
    const residences: Person["LifeEvents"] = [];

    for (const event of lifeEvents) {
        if (event.Type === LifeEventType.Residence) {
            if (event.Date.Year !== null && !!event.LocationText) {
                residences.push(event);
            }
        } else {
            nonResidence.push(event);
        }
    }

    residences.sort((a, b) => a.Date.Year! - b.Date.Year!);

    return [...nonResidence, ...residences];
};

export const getEventsDetails = <T extends { events: EventEntries }>(
    lifeEvents: Person["LifeEvents"],
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
    defaultYearProximity: number | null = null,
): T["events"] => {
    const eventGroups = obtainEventGroups(formMetadata);
    const bindings = obtainEventRowBindings(eventGroups);
    const maxInstancesMap = obtainMaxEventInstances(eventGroups, currentFormValue.events);

    const preparedEvents = prepareLifeEvents(lifeEvents, formMetadata);
    const currentEvents = cloneDeep(currentFormValue.events);
    cleanEventInstances(currentEvents, preparedEvents, bindings);
    populateEventInstances(currentEvents, preparedEvents, bindings, maxInstancesMap, defaultYearProximity);

    if (formMetadata.contentType === "collection" && currentEvents["SelfResidence"]) {
        for (const instance of currentEvents["SelfResidence"].instances) {
            instance.date = { year: null, month: null, day: null };
        }
    }

    alignEventRows(currentEvents, eventGroups, defaultYearProximity);

    return currentEvents;
};

const obtainNameGroups = (formMetadata: FormMetadataResponse) =>
    formMetadata.fieldGroupsAndFeatureContainers.filter(
        (fieldGroupOrFeature) => "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === "name",
    );
const obtainAllowedNameFields = (nameGroups: NameFieldGroup[]) => {
    const foundAssertions = new Set<NameAssertion>();
    nameGroups.forEach((nameGroup) => {
        nameGroup.fields.forEach((field) => {
            foundAssertions.add(field.assertion);
        });
    });
    return foundAssertions;
};

const obtainRequiredNameFields = (nameGroups: NameFieldGroup[]) => {
    const requiredAssertions = new Set<NameAssertion>();
    nameGroups.forEach((nameGroup) => {
        nameGroup.fields.forEach((field) => {
            if (field.features.required) requiredAssertions.add(field.assertion);
        });
    });
    return requiredAssertions;
};

export const getNameDetails = <T extends { name: any }>(
    person: Person,
    formMetadata: FormMetadataResponse,
): T["name"] => {
    const nameGroups = obtainNameGroups(formMetadata);
    const foundAssertions = obtainAllowedNameFields(nameGroups);
    // A name field required for search is locked to Exact, so seed its exactness exact-only when
    // applying a quick-fill/suggestion person — matching the metadata converter baseline. See TT-3965.
    const requiredAssertions = obtainRequiredNameFields(nameGroups);

    return {
        givenName: foundAssertions.has(NameAssertion.GivenName) ? person.GivenName : "",
        surname: foundAssertions.has(NameAssertion.Surname) ? person.FatherSurname || person.Surname : "",
        secondSurname: foundAssertions.has(NameAssertion.SecondSurname) ? person.MotherSurname : "",
        givenNameExactness: {
            flags: {
                phonetic: false,
                similar: false,
                initials: false,
            },
            isExact: requiredAssertions.has(NameAssertion.GivenName),
        },
        surnameExactness: {
            flags: {
                phonetic: false,
                similar: false,
                soundex: false,
            },
            isExact: requiredAssertions.has(NameAssertion.Surname),
        },
    };
};

const GENDER_FIELD_KEY = "SelfGender";

const hasGenderField = (formMetadata: FormMetadataResponse) => {
    for (const fieldGroupOrFeature of formMetadata.fieldGroupsAndFeatureContainers) {
        const isOtherGroup = "groupType" in fieldGroupOrFeature && fieldGroupOrFeature.groupType === GroupType.Other;

        if (isOtherGroup) {
            const otherGroup = fieldGroupOrFeature as OtherFieldGroup;
            const hasGender = otherGroup.others.some(
                (other) => other.assertion === SpecialAssertion.Gender && other.relation === "Self",
            );
            if (hasGender) return true;
        }
    }

    return false;
};

export const getGenderDetails = <T extends { fields: Record<string, any> }>(
    person: Person,
    currentFormValue: T,
    formMetadata: FormMetadataResponse,
): T["fields"] => {
    const isGenderFieldAllowed = hasGenderField(formMetadata);

    const currentFields = structuredClone(currentFormValue.fields);

    if (isGenderFieldAllowed) {
        currentFields[GENDER_FIELD_KEY] = {
            ...currentFields[GENDER_FIELD_KEY],
            value: person.Gender ?? "",
        };
    }

    return currentFields;
};

export const getTimespanDetails = <
    T extends {
        timespan: {
            isVisible: boolean;
            yearStart: number | null;
            yearEnd: number | null;
            matchMode: "rankOnly" | "mustMatch";
        };
    },
>(
    baseFormValues: T,
    formMetadata: FormMetadataResponse,
    currentFormValues: T,
): T["timespan"] => {
    const timespanFeature = formMetadata.features.find((f) => f.featureType === FormLevelFeatureType.Timespan);
    let timespan = structuredClone(baseFormValues.timespan);
    if (timespanFeature) {
        const hadTimespan = currentFormValues.timespan.isVisible;
        if (!hadTimespan) {
            timespan.isVisible = false;
        } else {
            timespan = {
                yearStart: timespanFeature.attributes.sliderRange.min,
                yearEnd: timespanFeature.attributes.sliderRange.max,
                matchMode: "rankOnly",
                isVisible: true,
            };
        }
    }

    return timespan;
};
